import React, { useEffect, useRef, useState } from "react";
import "./ProjectDropdown.css";
import { loginRequest } from "../msalConfig";
import { useMsal } from "@azure/msal-react";

interface Props {
    onSelect: (project: string | null) => void;
}

const ProjectDropdown: React.FC<Props> = ({ onSelect }) => {
    const { instance } = useMsal();

    const [projects, setProjects] = useState<string[]>([]);
    const [filteredProjects, setFilteredProjects] = useState<string[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedProject, setSelectedProject] = useState<string | null>(null);
    const [isOpen, setIsOpen] = useState(false);

    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Fetch project list from backend
    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const [authToken] = await Promise.all([
                    instance.acquireTokenSilent(loginRequest).then(res => res.accessToken),
                ]);

                const response = await fetch(`${process.env.REACT_APP_BACKEND_BASE_URL}/projects/project-list`, {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        "Content-Type": "application/json",
                    },
                });

                if (!response.ok) throw new Error("Failed to fetch projects");

                const data: string[] = await response.json();

                setProjects(data);
                setFilteredProjects(data);
            } catch (error) {
                console.error("Error fetching projects:", error);
            }
        };

        fetchProjects();
    }, [instance]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Get value shown in the input
    const getDisplayValue = () => {
        return isOpen ? searchTerm : selectedProject ?? "";
    };

    // Filter projects on input change
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setSearchTerm(val);
        setIsOpen(true);

        if (val.trim() === "") {
            setFilteredProjects(projects);
            setSelectedProject(null);
            onSelect(null);
        } else {
            const filtered = projects.filter((p) =>
                p.toLowerCase().includes(val.toLowerCase())
            );
            setFilteredProjects(filtered);
        }
    };

    // When a project is selected
    const handleSelect = (project: string) => {
        setSelectedProject(project);
        setSearchTerm(project);
        setIsOpen(false);
        onSelect(project);
    };

    return (
        <div className="dropdown" ref={wrapperRef}>
            <div className="control">
                <div className="selected-value">
                    <input
                        ref={inputRef}
                        type="text"
                        value={getDisplayValue()}
                        onChange={handleInputChange}
                        onClick={() => setIsOpen(true)}
                        placeholder="Select template project"
                    />
                </div>
                <div className={`arrow ${isOpen ? "open" : ""}`}></div>
            </div>

            <div className={`options ${isOpen ? "open" : ""}`}>
                {filteredProjects.map((project) => (
                    <div
                        key={project}
                        className={`option ${selectedProject === project ? "selected" : ""}`}
                        onClick={() => handleSelect(project)}
                    >
                        {project}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ProjectDropdown;
